// ABOUTME: Thin fetch client of the job REST API (/api/job): jobs, attempts logs, types, schedules.
// ABOUTME: Errors surface as JobApiError carrying the server `message` and status, never raw text.

import {
    JobListResponse,
    JobLogPage,
    JobResources,
    Jobs,
    JobSchedule,
    JobState,
    JobTypeProjection,
    LogLevel,
} from '../types';

const BASE = '/api/job';

export class JobApiError extends Error {
    readonly status: number;

    readonly details: string[];

    constructor(message: string, status: number, details: string[] = []) {
        super(message);
        this.name = 'JobApiError';
        this.status = status;
        this.details = details;
    }
}

/** The text a snackbar shows for a failed call: the server message plus its validation details. */
export const jobApiErrorMessage = (error: unknown): string => {
    if (error instanceof JobApiError) {
        return error.details.length
            ? `${error.message}: ${error.details.join('; ')}`
            : error.message;
    }
    return error instanceof Error ? error.message : String(error);
};

const readError = async (response: Response): Promise<JobApiError> => {
    const body = (await response.json().catch(() => undefined)) as
        | { message?: unknown; details?: unknown }
        | undefined;
    const message =
        typeof body?.message === 'string' && body.message
            ? body.message
            : response.statusText || `HTTP ${response.status}`;
    const details = Array.isArray(body?.details)
        ? body.details.filter((detail): detail is string => typeof detail === 'string')
        : [];
    return new JobApiError(message, response.status, details);
};

async function request<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
    const { json, ...rest } = init ?? {};
    const response = await fetch(`${BASE}${path}`, {
        credentials: 'include',
        ...rest,
        ...(json !== undefined
            ? {
                  body: JSON.stringify(json),
                  headers: { 'Content-Type': 'application/json', ...(rest.headers ?? {}) },
              }
            : {}),
    });
    if (!response.ok) {
        throw await readError(response);
    }
    if (response.status === 204) {
        return undefined as T;
    }
    return (await response.json()) as T;
}

const enc = encodeURIComponent;

export type JobListQuery = {
    type?: string;
    state?: JobState | JobState[];
    user?: string;
    scheduleId?: string;
    cursor?: string;
    limit?: number;
};

export const listJobs = (query: JobListQuery) => {
    const params = new URLSearchParams();
    if (query.type) params.set('type', query.type);
    const states = Array.isArray(query.state) ? query.state : [query.state];
    const state = states.filter(Boolean).join(',');
    if (state) params.set('state', state);
    if (query.user) params.set('user', query.user);
    if (query.scheduleId) params.set('scheduleId', query.scheduleId);
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.limit) params.set('limit', String(query.limit));
    return request<JobListResponse>(`/jobs?${params.toString()}`);
};

export const getJob = (id: string) => request<Jobs>(`/jobs/${enc(id)}`);

export const getAttemptLogs = (
    id: string,
    n: number,
    options: { level?: LogLevel[]; afterSeq?: number; limit?: number } = {},
) => {
    const params = new URLSearchParams();
    if (options.level?.length) params.set('level', options.level.join(','));
    if (options.afterSeq) params.set('afterSeq', String(options.afterSeq));
    if (options.limit) params.set('limit', String(options.limit));
    return request<JobLogPage>(`/jobs/${enc(id)}/attempts/${n}/logs?${params.toString()}`);
};

export type LaunchJobInput = {
    type: string;
    args?: Record<string, unknown>;
    resources?: JobResources | null;
    skipConcurrencyCheck?: boolean;
};

/** Starts a job: the server answers 202 with the new job id. */
export const launchJob = (input: LaunchJobInput) =>
    request<{ id: string }>('/jobs', { method: 'POST', json: input });

export const retryJob = (id: string) =>
    request<{ id: string; attempt: number }>(`/jobs/${enc(id)}/retry`, { method: 'POST' });

export const cancelJob = (id: string) =>
    request<{ id: string }>(`/jobs/${enc(id)}/cancel`, { method: 'POST' });

export const deleteJob = (id: string) => request<void>(`/jobs/${enc(id)}`, { method: 'DELETE' });

export const listJobTypes = () => request<{ types: JobTypeProjection[] }>('/types');

export const jobAttachmentUrl = (id: string) => `${BASE}/jobs/${enc(id)}/attachment`;

const filenameFromDisposition = (disposition: string | null): string | undefined => {
    const match = /filename\*?=(?:UTF-8'')?("?)([^";\n]+)\1/i.exec(disposition ?? '');
    return match ? decodeURIComponent(match[2]) : undefined;
};

/** Downloads the job attachment (the file of its latest attempt that produced one). */
export const downloadJobAttachment = async (id: string, fallbackName?: string) => {
    const response = await fetch(jobAttachmentUrl(id), { credentials: 'include' });
    if (!response.ok) {
        throw await readError(response);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
        filenameFromDisposition(response.headers.get('Content-Disposition')) ??
        fallbackName ??
        'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export type ScheduleInput = {
    type: string;
    args?: Record<string, unknown>;
    cron: string;
    timezone?: string;
    enabled?: boolean;
    concurrencyPolicy?: 'forbid' | 'allow';
    startingDeadlineSeconds?: number;
    resources?: JobResources | null;
};

export type SchedulePatch = Partial<Omit<ScheduleInput, 'type'>>;

export const listSchedules = () => request<{ schedules: JobSchedule[] }>('/schedules');

export const createSchedule = (input: ScheduleInput) =>
    request<JobSchedule>('/schedules', { method: 'POST', json: input });

export const updateSchedule = (id: string, patch: SchedulePatch) =>
    request<JobSchedule>(`/schedules/${enc(id)}`, { method: 'PATCH', json: patch });

export const deleteSchedule = (id: string) =>
    request<void>(`/schedules/${enc(id)}`, { method: 'DELETE' });

export const runScheduleNow = (id: string) =>
    request<{ id: string }>(`/schedules/${enc(id)}/run-now`, { method: 'POST' });
